import ScenarioActionType from '@/store/modules/scenario/scenario.type'
import ScenarioStoreMixin from '@/store/modules/scenario/mixins/scenario.mixin'
import StepStoreMixin from '@/store/modules/scenario/mixins/step.mixin'
import CardStoreMixin from '@/store/modules/scenario/mixins/card.mixin'
import StepConfigStoreMixin from '@/store/modules/scenario/mixins/step_config.mixin'
import { VariableStoreAction, VariableStoreMutation } from '@/store/modules/scenario/mixins/variable.mixin'
import { CardType } from '@/utils/const'

const types = ScenarioActionType

const state = {
    draggingCard: false,
    savingCount: 0,
    shouldUpdateGraph: 0,
    // active
    activeGraphStepNodeId: null,
    activeGraphCardNodeId: null,
    activeScenarioCode: null,
    activeStepCode: null,
    activeCardCode: null,
    activeScenario: null,
    activeStep: null,
    activeCard: null,
    // special
    defaultScenarioCode: null,
    reminderStepCode: null,
    startSessionStepCode: null,
    startSentenceStepCode: null,
    allowReminder: false,
    // mapping
    mapCodeToScenario: {},
    mapCodeToStep: {},
    mapNameToStep: {},
    mapCodeToCard: {},
    mapIntentToStep: {},
    // error and warning
    errorList: [],
    // data
    scenarioList: [],
    stepList: [],
    cardList: [],
    intentList: [],
    entityList: [],
    variableList: [],
    variableTypeList: [],
    tagList: [],
    appConfigList: [],
    webViewList: [],
    crm: null,
    crmList: [],
    dialogActList: [],
    supportCategoryList: [],
    graphNodeList: []
}

const mutations = {
    [types.DRAGGING_CARD]: (state, payload) => {
        state.draggingCard = payload
    },
    [types.MAPPING]: (state, payload) => {
        // mapping
        state.mapCodeToScenario = payload.mapCodeToScenario
        state.mapCodeToStep = payload.mapCodeToStep
        state.mapNameToStep = payload.mapNameToStep
        state.mapCodeToCard = payload.mapCodeToCard
        // data
        state.scenarioList = payload.scenarioList
        state.stepList = payload.stepList
        state.cardList = payload.cardList
        state.variableList = payload.variableList
        state.variableTypeList = payload.variableTypeList
        state.tagList = payload.tagList
        state.appConfigList = payload.appConfigList
        state.crm = payload.crm
        state.crmList = state.crm.built_in.concat(state.crm.attributes)
        // nlp
        state.intentList = payload.intentList
        state.entityList = payload.entityList

        for (let scenario of state.scenarioList) {
            if (scenario.name === 'Default Scenario') {
                state.defaultScenarioCode = scenario.code
                for (let step of scenario.steps) {
                    if (step.name === 'Reminder') {
                        state.reminderStepCode = step.code
                    } else if (step.name === 'Start Session') {
                        state.startSessionStepCode = step.code
                    } else if (step.name === 'Start Sentence') {
                        state.startSentenceStepCode = step.code
                    }
                }
                break
            }
        }
        state.allowReminder = false
        for (let appConfig of state.appConfigList) {
            if (appConfig.config_key === 'allow_bot_reminder') {
                if (appConfig.config_value === '1') {
                    state.allowReminder = true
                }
                break
            }
        }
    },
    [types.ADD_SAVING_COUNT]: (state, payload) => {
        state.savingCount += payload
    },
    [types.SHOULD_UPDATE_GRAPH]: (state) => {
        state.shouldUpdateGraph = new Date().getTime()
    },
    [types.WEB_VIEW_LIST]: (state, payload) => {
        state.webViewList = payload
    },
    [types.ACTIVE_DATA]: (state, payload) => {
        let {
            activeGraphStepNodeId,
            activeGraphCardNodeId,
            activeCardCode,
            activeStepCode,
            activeScenarioCode
        } = payload
        state.activeGraphCardNodeId = activeGraphCardNodeId || null
        if (activeGraphCardNodeId) {
            let activeGraphCardNode = state.graphNodeList.find(node => node.id === activeGraphCardNodeId)
            if (activeGraphCardNode) {
                activeCardCode = activeGraphCardNode.data.data.code
                activeGraphStepNodeId = activeGraphCardNode.parent.id
            }
        }
        state.activeGraphStepNodeId = activeGraphStepNodeId || null
        let activeGraphStepNode = null
        if (activeGraphStepNodeId) {
            activeGraphStepNode = state.graphNodeList.find(node => node.id === activeGraphStepNodeId)
            if (activeGraphStepNode) {
                activeStepCode = activeGraphStepNode.data.data.code
            }
        }
        state.activeCardCode = activeCardCode || null
        if (activeCardCode) {
            state.activeCard = state.mapCodeToCard[activeCardCode]
            activeStepCode = state.activeCard.step_code
        } else {
            state.activeCard = null
        }
        if (!state.mapCodeToStep[activeStepCode]) {
            activeStepCode = null
        }
        state.activeStepCode = activeStepCode || null
        if (activeStepCode) {
            state.activeStep = state.mapCodeToStep[activeStepCode]
            if (activeGraphStepNode) {
                if (activeGraphStepNode.data.data.code !== activeStepCode) {
                    state.activeGraphStepNodeId = null
                }
                activeScenarioCode = state.activeScenarioCode
            } else {
                activeGraphStepNode = state.graphNodeList.find(node => node.data.data.code === activeStepCode)
                activeScenarioCode = activeGraphStepNode ? state.activeScenarioCode : state.activeStep.scenario_code
            }
        } else {
            state.activeStep = null
        }
        state.activeScenarioCode = activeScenarioCode || null
        state.activeScenario = activeScenarioCode ? state.mapCodeToScenario[activeScenarioCode] : null
        state.shouldUpdateGraph = new Date().getTime()
    },
    [types.MAP_INTENT_TO_STEP]: (state) => {
        let mapIntentToStep = {}
        state.stepList.forEach(step => {
            step.config.forEach(trigger => {
                mapIntentToStep[trigger.config] = step.code
            })
        })
        state.mapIntentToStep = mapIntentToStep
    },
    [types.SCAN_ERROR]: (state, payload) => {
        state.errorList = payload
    },
    [types.DIALOG_ACT]: (state, payload) => {
        state.dialogActList = payload
    },
    [types.SUPPORT_CATEGORY_LIST]: (state, payload) => {
        state.supportCategoryList = payload
    },
    [types.GRAPH_NODE_LIST]: (state, payload) => {
        state.graphNodeList = payload
    },
    ...VariableStoreMutation
}

const actions = {
    [types.MAPPING] (context, payload) {
        context.commit(types.MAPPING, payload)
        context.commit(types.MAP_INTENT_TO_STEP)
        context.dispatch(types.SCAN_ERROR)
    },
    [types.SHOULD_UPDATE_GRAPH]: (context) => {
        context.commit(types.SHOULD_UPDATE_GRAPH)
        context.dispatch(types.SCAN_ERROR)
    },
    [types.WEB_VIEW_LIST] ({ commit }, payload) {
        commit(types.WEB_VIEW_LIST, payload)
    },
    [types.ACTIVE_DATA]: (context, payload) => {
        context.commit(types.ACTIVE_DATA, payload)
    },
    [types.DIALOG_ACT]: (context, payload) => {
        context.commit(types.DIALOG_ACT, payload)
    },
    [types.SUPPORT_CATEGORY_LIST]: (context, payload) => {
        context.commit(types.SUPPORT_CATEGORY_LIST, payload)
    },
    [types.GRAPH_NODE_LIST]: (context, payload) => {
        context.commit(types.GRAPH_NODE_LIST, payload)
    },
    [types.MAP_INTENT_TO_STEP]: (context) => {
        context.commit(types.MAP_INTENT_TO_STEP)
    },
    [types.SCAN_ERROR]: (context) => {
        let stepList = context.state.stepList
        let variableList = context.state.variableList
        let mapVariable = {}
        variableList.forEach(variable => {
            mapVariable[variable.name] = variable
        })
        let errorList = []
        for (let step of stepList) {
            if (step.activate === 0) {
                continue
            }
            if (step.cards.length < 1) {
                errorList.push({
                    title: `Step "${step.name}" has no card`,
                    priority: 1,
                    data: {
                        type: 'step',
                        step: step
                    }
                })
                continue
            }
            for (let card of step.cards) {
                if (card.activate === 0) {
                    continue
                }
                if (card.card_type_id === CardType.CAROUSEL) {
                    for (let carousel of card.config.carousel_cards) {
                        if (!carousel.title) {
                            errorList.push({
                                title: `Card "${card.name}" has a carousel with no title`,
                                priority: 2,
                                data: {
                                    type: 'card',
                                    card: card
                                }
                            })
                        }
                    }
                } else if (card.card_type_id === CardType.IMAGE) {
                    if (!card.config.title) {
                        errorList.push({
                            title: `Card "${card.name}" image has no title`,
                            priority: 2,
                            data: {
                                type: 'card',
                                card: card
                            }
                        })
                    }
                } else if (card.card_type_id === CardType.TEXT) {
                    if (!card.config.text) {
                        errorList.push({
                            title: `Card "${card.name}" has no content`,
                            priority: 2,
                            data: {
                                type: 'card',
                                card: card
                            }
                        })
                    }
                } else if (card.card_type_id === CardType.FORM) {
                    if (card.config.properties) {
                        for (let property of card.config.properties) {
                            if (!mapVariable[property.title]) {
                                errorList.push({
                                    title: `Card "${card.name}" contain deleted variable`,
                                    priority: 1,
                                    data: {
                                        type: 'card',
                                        card: card
                                    }
                                })
                            }
                        }
                    }
                }
            }
        }
        errorList.sort((a, b) => a.priority - b.priority)
        context.commit(types.SCAN_ERROR, errorList)
    },
    ...ScenarioStoreMixin,
    ...StepStoreMixin,
    ...CardStoreMixin,
    ...StepConfigStoreMixin,
    ...VariableStoreAction
}

export default {
    namespaced: true,
    state,
    mutations,
    actions
}
